import React, { useMemo, useState, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  useReactFlow,
  Panel,
  MarkerType,
  OnConnect,
  OnConnectEnd,
} from '@xyflow/react';
import CustomNode from '@/components/CustomNode';

interface FlowWithControlsProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: OnConnect;
  onConnectEnd: OnConnectEnd;
  getLayoutedElements: (
    nodes: Node[],
    edges: Edge[],
    direction?: string
  ) => Promise<{ nodes: Node[]; edges: Edge[] }>;
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  isLayouting: boolean;
}

const FlowWithControls: React.FC<FlowWithControlsProps> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onConnectEnd,
  getLayoutedElements,
  setNodes,
  setEdges,
  isLayouting,
}) => {
  const reactFlowInstance = useReactFlow();
  const [focusedEdgeId, setFocusedEdgeId] = useState<string | null>(null);

  // Handle edge focus
  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    setFocusedEdgeId(edge.id);
  }, []);

  // Define node types
  const nodeTypes = useMemo(
    () => ({
      customNode: CustomNode,
    }),
    []
  );

  // Define default edge options
  const defaultEdgeOptions = useMemo(
    () => ({
      type: 'default',
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
      },
      style: { strokeWidth: 2 },
      animated: false,
    }),
    []
  );

  // Apply z-index to focused edges
  const styledEdges = useMemo(() => {
    return edges.map((edge) => ({
      ...edge,
      markerEnd: edge.markerEnd
        ? {
            ...(edge.markerEnd as object),
            ...(edge.id === focusedEdgeId ? { color: '#3b82f6' } : {}),
          }
        : undefined,
      markerStart: edge.markerStart
        ? {
            ...(edge.markerStart as object),
            type: (edge.markerStart as any).type || MarkerType.Arrow, // Ensure type property exists
            ...(edge.id === focusedEdgeId ? { color: '#3b82f6' } : {}),
          }
        : undefined,
      ...(edge.id === focusedEdgeId ? { zIndex: 1000 } : {}),
      style: {
        ...edge.style,
        ...(edge.id === focusedEdgeId
          ? {
              stroke: '#3b82f6',
              strokeWidth: 3,
            }
          : {}),
      },
    }));
  }, [edges, focusedEdgeId]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={styledEdges as Edge[]}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onConnectEnd={onConnectEnd}
      onEdgeClick={onEdgeClick}
      nodeTypes={nodeTypes}
      defaultEdgeOptions={defaultEdgeOptions}
      fitView
      edgesFocusable={true}
      // edgesUpdatable={true}
      elementsSelectable={true}
    >
      <Background />
      <Controls />
      <MiniMap />
      <Panel position="top-right">
        <button
          onClick={async () => {
            const { nodes: layoutedNodes, edges: layoutedEdges } = await getLayoutedElements(
              nodes,
              edges
            );
            setNodes([...layoutedNodes]);
            setEdges([...layoutedEdges]);
            window.requestAnimationFrame(() => reactFlowInstance.fitView());
          }}
          className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
          disabled={isLayouting}
        >
          {isLayouting ? 'Applying Layout...' : 'Apply Auto Layout'}
        </button>
      </Panel>
    </ReactFlow>
  );
};

export default FlowWithControls;
